import Dexie, { type Table } from "dexie";
import type {
  SkuMaster,
  DailySnapshot,
  InventoryLayer,
  Campaign,
  Promotion,
  ManualPromotion,
  Alert,
  GlobalConfig,
  CloudConfig,
  WarehouseProvider,
  WarehouseMapping,
  EstimateInput,
  TodoItem,
  CalculationRecord,
  Shop,
  OpsLog,
} from "./types";
import type { Site, SiteConfig, CrossSiteSummary, CrossSiteReport, ExchangeRate, DiagnosisNote } from "./types";
import type { ShipmentFormulaConfig, ProfitFormulaConfig } from "./types";
import { countShopMskus } from "./shopFilter";
import { normalizeRuleSet, cloneDefaultRules, type DiagnosisRuleSet } from "./diagnosisRules";
import {
  DEFAULT_WAREHOUSE_REGIONS,
  DEFAULT_SHIPMENT_FORMULA,
  DEFAULT_PROFIT_FORMULA,
} from "./types";

/**
 * Amazon Operation OS — Local storage layer (IndexedDB via Dexie).
 * Every module reads from these tables. There is no duplicated data anywhere.
 */
export class AmzOpsDB extends Dexie {
  skuMaster!: Table<SkuMaster, [string, string]>;
  dailySnapshot!: Table<DailySnapshot, number>;
  inventoryLayer!: Table<InventoryLayer, number>;
  campaigns!: Table<Campaign, string>;
  promotions!: Table<Promotion, string>;
  manualPromotions!: Table<ManualPromotion, string>;
  alerts!: Table<Alert, string>;
  config!: Table<{ key: string; value: unknown }, string>;
  estimates!: Table<CalculationRecord, string>;
  warehouseProviders!: Table<WarehouseProvider, string>;
  warehouseMappings!: Table<WarehouseMapping, number>;
  calculationRecords!: Table<CalculationRecord, string>;
  todos!: Table<TodoItem, string>;
  shops!: Table<Shop, string>;
  opsLogs!: Table<OpsLog, string>;
  sites!: Table<Site, string>;
  exchangeRates!: Table<ExchangeRate, string>;
  diagnosisNotes!: Table<DiagnosisNote, string>;

  constructor() {
    super("amazon-ops-os");
    this.version(3).stores({
      skuMaster: "sku, store, fulfillment, saleStatus, owner, category, lifecycle",
      dailySnapshot: "++id, [sku+date], date, sku",
      inventoryLayer: "++id, [sku+date], date, sku",
      campaigns: "id, startDate, endDate, active",
      promotions: "id, sku, store, type, status, startDate, endDate",
      alerts: "id, sku, type, severity, status, date",
      config: "key",
      warehouseProviders: "id",
      estimates: "id",
      calculationRecords: "id, sku, createdAt",
    });
    this.version(4).stores({
      skuMaster: "sku, store, fulfillment, saleStatus, owner, category, lifecycle",
      dailySnapshot: "++id, [sku+date], date, sku",
      inventoryLayer: "++id, [sku+date], date, sku",
      campaigns: "id, startDate, endDate, active",
      promotions: "id, sku, store, type, status, startDate, endDate",
      alerts: "id, sku, type, severity, status, date",
      config: "key",
      warehouseProviders: "id",
      estimates: "id",
      todos: "id, completed, dueDate",
    });
    this.version(5).stores({
      skuMaster: "sku, store, fulfillment, saleStatus, owner, category, lifecycle",
      dailySnapshot: "++id, [sku+date], date, sku",
      inventoryLayer: "++id, [sku+date], date, sku",
      campaigns: "id, startDate, endDate, active",
      promotions: "id, sku, store, type, status, startDate, endDate",
      manualPromotions: "id, sku, type, startDate, endDate",
      alerts: "id, sku, type, severity, status, date",
      config: "key",
      warehouseProviders: "id",
      estimates: "id",
      todos: "id, completed, dueDate",
    });
    this.version(6).stores({
      skuMaster: "sku, store, fulfillment, saleStatus, owner, category, lifecycle",
      dailySnapshot: "++id, [sku+date], date, sku",
      inventoryLayer: "++id, [sku+date], date, sku",
      campaigns: "id, startDate, endDate, active",
      promotions: "id, sku, store, type, status, startDate, endDate",
      manualPromotions: "id, sku, type, startDate, endDate",
      alerts: "id, sku, type, severity, status, date",
      config: "key",
      warehouseProviders: "id",
      estimates: "id",
      todos: "id, completed, dueDate",
      shops: "id, name, createdAt",
    });
    this.version(7).stores({
      skuMaster: "sku, store, fulfillment, saleStatus, owner, category, lifecycle",
      dailySnapshot: "++id, [sku+date], date, sku",
      inventoryLayer: "++id, [sku+date], date, sku",
      campaigns: "id, startDate, endDate, active",
      promotions: "id, sku, store, type, status, startDate, endDate",
      manualPromotions: "id, sku, type, startDate, endDate",
      alerts: "id, sku, type, severity, status, date",
      config: "key",
      warehouseProviders: "id",
      estimates: "id",
      todos: "id, completed, dueDate",
      shops: "id, name, createdAt",
      opsLogs: "id, sku, date, action",
    });
    this.version(8).stores({
      warehouseMappings: "++id, warehouseName, region",
    });
    this.version(9).stores({
      skuMaster: "sku, store, siteId, fulfillment, saleStatus, owner, category, lifecycle",
      dailySnapshot: "++id, [sku+date], date, sku, siteId",
      inventoryLayer: "++id, [sku+date], date, sku, siteId",
      campaigns: "id, siteId, startDate, endDate, active",
      promotions: "id, sku, store, siteId, type, status, startDate, endDate",
      manualPromotions: "id, sku, siteId, type, startDate, endDate",
      alerts: "id, sku, siteId, type, severity, status, date",
      config: "key",
      warehouseProviders: "id",
      warehouseMappings: "++id, warehouseName, region",
      calculationRecords: "id, sku, siteId, createdAt",
      todos: "id, siteId, completed, dueDate",
      shops: "id, name, siteId, createdAt",
      opsLogs: "id, sku, siteId, date, action",
      sites: "id, name, marketplace, currency, isActive, sortOrder",
    });
    // Step 1: Backup skuMaster data and delete original table
    // (Dexie doesn't support changing primary key directly, so we use backup+restore)
    this.version(10).stores({
      skuMasterBackup: "sku, store, siteId",
      // Omitting skuMaster deletes it
    }).upgrade(async (tx) => {
      // Copy all data from old skuMaster to backup before it's deleted
      const all = await tx.table("skuMaster").toArray();
      await tx.table("skuMasterBackup").bulkPut(all);
    });

    // Step 2: Create new skuMaster with [sku+siteId] compound key + restore from backup
    this.version(11).stores({
      skuMaster: "[sku+siteId], store, siteId, fulfillment, saleStatus, owner, category, lifecycle",
    }).upgrade(async (tx) => {
      const all = await tx.table("skuMasterBackup").toArray();
      const fixed = all.map((item: any) => ({
        ...item,
        siteId: item.siteId || "site_us",
      }));
      await tx.table("skuMaster").bulkPut(fixed);
    });

    // Step 3: Delete backup table
    this.version(12).stores({
      // Omitting skuMasterBackup deletes it
    });

    // Step 4: Fix records with missing/empty siteId (caused by import page using single-string keys)
    this.version(13).stores({
      // No schema change, just data fix
    }).upgrade(async (tx) => {
      const all = await tx.table("skuMaster").toArray();
      const needsFix = all.filter((r: any) => !r.siteId || r.siteId === "" || r.siteId === "undefined");
      if (needsFix.length === 0) return;
      // Clear and re-insert all with guaranteed siteId
      await tx.table("skuMaster").clear();
      const fixed = all.map((r: any) => ({
        ...r,
        siteId: r.siteId || "site_us",
      }));
      await tx.table("skuMaster").bulkPut(fixed);
    });

    // Step 5: Backfill siteId for dailySnapshot & inventoryLayer historical data
    this.version(14).stores({
      // No schema change, just data fix
    }).upgrade(async (tx) => {
      for (const table of ["dailySnapshot", "inventoryLayer"]) {
        const all = await tx.table(table).toArray();
        const needsFix = all.filter((r: any) => !r.siteId || r.siteId === "" || r.siteId === "undefined");
        if (needsFix.length === 0) continue;
        const fixed = needsFix.map((r: any) => ({ ...r, siteId: "site_us" }));
        await tx.table(table).bulkPut(fixed);
      }
    });

    // Step 6: 全局多币种汇率表（任意货币对，用户自定义）。
    // 已有站点级汇率（exchangeRateToUsd / cnyToUsdRate）迁移为全局货币对，避免用户已改的汇率丢失；
    // 全新数据库（无站点数据）的默认汇率由 ensureDefaultExchangeRates 在 UI 挂载时懒填充。
    this.version(15).stores({
      exchangeRates: "id, from, to",
    }).upgrade(async (tx) => {
      let siteRows: Array<Record<string, unknown>> = [];
      try {
        siteRows = await tx.table("sites").toArray();
      } catch {
        siteRows = [];
      }
      if (siteRows.length === 0) return;
      const rateMap = new Map<string, { from: string; to: string; rate: number }>();
      for (const s of siteRows) {
        const cur = normalizeCurrencyCode(String(s?.currency ?? ""));
        const r2u = Number(s?.exchangeRateToUsd);
        if (cur && cur !== "USD" && Number.isFinite(r2u) && r2u > 0) {
          rateMap.set(`${cur}_USD`, { from: cur, to: "USD", rate: r2u });
        }
      }
      const usSite = siteRows.find((s) => normalizeCurrencyCode(String(s?.currency ?? "")) === "USD") ?? siteRows[0];
      const cnyRate = Number(usSite?.cnyToUsdRate) > 0 ? Number(usSite.cnyToUsdRate) : 7.25;
      rateMap.set("USD_CNY", { from: "USD", to: "CNY", rate: cnyRate });
      const now = new Date().toISOString();
      const rows = [...rateMap.values()].map((r) => ({ id: `${r.from}_${r.to}`, ...r, updatedAt: now }));
      await tx.table("exchangeRates").bulkPut(rows);
    });

    // Step 7: 异常诊断备注（留痕）。新增表，不影响既有数据。
    this.version(16).stores({
      diagnosisNotes: "id, sku, siteId, anomalyType, createdAt",
    });
  }
}

export const db = new AmzOpsDB();

const DB_NAME = "amazon-ops-os";
const UPGRADE_KEY_CHANGE_ERR = /changing primary key/i;

/**
 * Raw-dump the contents of every object store in the existing database using a
 * versionless IndexedDB connection (no Dexie schema upgrade), so data can be
 * recovered even when the schema upgrade itself is unsupported.
 */
async function dumpAllObjectStores(): Promise<Record<string, unknown[]>> {
  const idb = await new Promise<IDBDatabase>((resolve, reject) => {
    if (!(globalThis as Record<string, unknown>).indexedDB) {
      reject(new Error("IndexedDB 不可用"));
      return;
    }
    const req = (globalThis as { indexedDB: IDBFactory }).indexedDB.open(DB_NAME);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("打开数据库失败"));
  });
  const dump: Record<string, unknown[]> = {};
  try {
    for (const storeName of Array.from(idb.objectStoreNames)) {
      const rows = await new Promise<unknown[]>((resolve, reject) => {
        const tx = idb.transaction(storeName, "readonly");
        const getReq = tx.objectStore(storeName).getAll();
        getReq.onsuccess = () => resolve(getReq.result);
        getReq.onerror = () => reject(getReq.error ?? new Error("读取数据失败"));
      });
      dump[storeName] = rows;
    }
  } finally {
    idb.close();
  }
  return dump;
}

/**
 * Open the database. Older browser databases whose `skuMaster` primary key
 * predates the `[sku+siteId]` compound key cannot be upgraded in place by
 * Dexie ("Not yet support for changing primary key"). In that case we rescue
 * all existing data, rebuild the database with the current schema, and restore
 * it — so users are never left on an infinite loading screen.
 */
export async function ensureDatabaseOpen(): Promise<void> {
  try {
    await db.open();
    return;
  } catch (err) {
    const msg = String((err as { message?: unknown })?.message ?? err);
    if (!err || !UPGRADE_KEY_CHANGE_ERR.test(msg)) {
      try { await db.close(); } catch { /* noop */ }
      throw err;
    }
  }

  // Known schema-conflict: back up, tear down, rebuild.
  const dump = await dumpAllObjectStores();
  try { await db.close(); } catch { /* noop */ }
  await Dexie.delete(DB_NAME);
  await db.open();

  const skuSource = (dump["skuMasterBackup"]?.length ? dump["skuMasterBackup"] : dump["skuMaster"]) as
    | Array<Record<string, unknown>>
    | undefined;
  if (skuSource?.length) {
    await db.skuMaster.bulkPut(
      skuSource.map((r) => ({ ...r, siteId: r.siteId ? String(r.siteId) : "site_us" })) as SkuMaster[]
    );
  }
  for (const [table, rows] of Object.entries(dump)) {
    if (!rows.length) continue;
    if (table === "skuMaster" || table === "skuMasterBackup") continue;
    if (!db.tables.some((t) => t.name === table)) continue;
    await (db.table(table) as Table<unknown, unknown>).bulkPut(rows as unknown[]);
  }
}

// ==================== Config helpers ====================
export const DEFAULT_GLOBAL_CONFIG: GlobalConfig = {
  defaultLeadTime: 40,
  defaultSafetyStockDays: 30,
  defaultTargetCoverDays: 60,
  profitMarginThreshold: 5,
  adRatioThreshold: 30,
  targetAdRatio: 25,
  ratingDropThreshold: 0.2,
  returnRateThreshold: 5,
  returnBasis: "refund",
  lifecycleNewDays: 90,
  lifecycleGrowthDays: 180,
};

export async function getGlobalConfig(): Promise<GlobalConfig> {
  const row = await db.config.get("global");
  return (row?.value as GlobalConfig) ?? DEFAULT_GLOBAL_CONFIG;
}

export async function setGlobalConfig(cfg: GlobalConfig): Promise<void> {
  await db.config.put({ key: "global", value: cfg });
}

export async function getCloudConfig(): Promise<CloudConfig | null> {
  const row = await db.config.get("cloud");
  return (row?.value as CloudConfig) ?? null;
}

export async function setCloudConfig(cfg: CloudConfig): Promise<void> {
  await db.config.put({ key: "cloud", value: cfg });
}

// ==================== 发货建议公式 & 利润公式配置 ====================

/** 发货建议公式配置（config key "shipmentFormula"），字段缺失时用默认值补齐 */
export async function getShipmentFormulaConfig(): Promise<ShipmentFormulaConfig> {
  const row = await db.config.get("shipmentFormula");
  const value = row?.value as Partial<ShipmentFormulaConfig> | undefined;
  return { ...DEFAULT_SHIPMENT_FORMULA, ...(value ?? {}) };
}

export async function setShipmentFormulaConfig(cfg: ShipmentFormulaConfig): Promise<void> {
  await db.config.put({ key: "shipmentFormula", value: cfg });
}

/** 利润计算公式配置（config key "profitFormula"），字段缺失时用默认值补齐 */
export async function getProfitFormulaConfig(): Promise<ProfitFormulaConfig> {
  const row = await db.config.get("profitFormula");
  const value = row?.value as Partial<ProfitFormulaConfig> | undefined;
  const merged = { ...DEFAULT_PROFIT_FORMULA, ...(value ?? {}) };
  return {
    ...merged,
    costItems: { ...DEFAULT_PROFIT_FORMULA.costItems, ...(value?.costItems ?? {}) },
    formulas: { ...DEFAULT_PROFIT_FORMULA.formulas, ...(value?.formulas ?? {}) },
  };
}

export async function setProfitFormulaConfig(cfg: ProfitFormulaConfig): Promise<void> {
  await db.config.put({ key: "profitFormula", value: cfg });
}

// ==================== 诊断规则库（可自定义原因/规则） ====================
export async function getDiagnosisRules(): Promise<DiagnosisRuleSet> {
  const row = await db.config.get("diagnosisRules");
  return normalizeRuleSet(row?.value);
}

export async function setDiagnosisRules(rules: DiagnosisRuleSet): Promise<void> {
  await db.config.put({ key: "diagnosisRules", value: normalizeRuleSet(rules) });
}

export async function resetDiagnosisRules(): Promise<DiagnosisRuleSet> {
  const defaults = cloneDefaultRules();
  await setDiagnosisRules(defaults);
  return defaults;
}

// ==================== 数据健康报告（最近一次导入） ====================
export async function getLatestHealthReport<T = unknown>(): Promise<T | null> {
  const row = await db.config.get("latestHealthReport");
  return (row?.value as T) ?? null;
}

export async function setLatestHealthReport(report: unknown): Promise<void> {
  await db.config.put({
    key: "latestHealthReport",
    value: { ...(report as Record<string, unknown>), savedAt: new Date().toISOString() },
  });
}

// ==================== Shop management ====================
const DEFAULT_SHOPS = [
  { id: "shop_1", name: "一店", createdAt: new Date().toISOString() },
  { id: "shop_2", name: "二店", createdAt: new Date().toISOString() },
  { id: "shop_3", name: "三店", createdAt: new Date().toISOString() },
];

export async function ensureDefaultShops(): Promise<void> {
  const count = await db.shops.count();
  if (count > 0) return;

  // Collect existing unique store values from data
  const existingStores = new Set<string>();
  const skus = await db.skuMaster.toArray();
  for (const sku of skus) {
    if (sku.store) existingStores.add(sku.store);
  }
  const promos = await db.promotions.toArray();
  for (const promo of promos) {
    if (promo.store) existingStores.add(promo.store);
  }

  // Check if any existing stores are already shop IDs (shop_xxx format)
  const hasShopIds = [...existingStores].some((s) => s.startsWith("shop_"));

  if (hasShopIds) {
    // Some stores are already shop IDs, just create default shops
    await db.shops.bulkPut(DEFAULT_SHOPS);
  } else {
    // Create shops from existing store values (backward compatibility)
    const shopList: Shop[] = [];
    const storeToId = new Map<string, string>();
    let idx = 1;
    for (const store of existingStores) {
      const id = `shop_${idx}`;
      shopList.push({ id, name: store, createdAt: new Date().toISOString() });
      storeToId.set(store, id);
      idx++;
    }

    // If no existing stores, create default shops
    if (shopList.length === 0) {
      await db.shops.bulkPut(DEFAULT_SHOPS);
      return;
    }

    await db.shops.bulkPut(shopList);

    // Migrate skuMaster.store references
    for (const sku of skus) {
      const newId = storeToId.get(sku.store);
      if (newId) {
        await db.skuMaster.put({ ...sku, store: newId });
      }
    }

    // Migrate promotions.store references
    for (const promo of promos) {
      const newId = storeToId.get(promo.store);
      if (newId) {
        await db.promotions.put({ ...promo, store: newId });
      }
    }
  }
}

export async function getAllShops(): Promise<Shop[]> {
  return db.shops.toArray();
}

export async function addShop(name: string): Promise<Shop> {
  const shop: Shop = {
    id: `shop_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    name: name.trim(),
    createdAt: new Date().toISOString(),
  };
  await db.shops.put(shop);
  return shop;
}

export async function renameShop(id: string, newName: string): Promise<void> {
  const shop = await db.shops.get(id);
  if (!shop) throw new Error("店铺不存在");
  await db.shops.put({ ...shop, name: newName.trim() });
}

export async function deleteShop(id: string): Promise<void> {
  const shop = await db.shops.get(id);
  await db.shops.delete(id);
  if (!shop) return;
  // Clear store references only within this shop's site; same sku may exist on other sites
  const siteId = shop.siteId ?? "site_us";
  if (siteId === "site_all") {
    await db.skuMaster.where("store").equals(id).modify({ store: "" });
    await db.promotions.where("store").equals(id).modify({ store: "" });
  } else {
    const skus = await db.skuMaster.where("store").equals(id).toArray();
    const toClear = skus.filter((s) => (s.siteId ?? "site_us") === siteId);
    for (const sku of toClear) {
      await db.skuMaster.put({ ...sku, store: "" });
    }
    const promos = await db.promotions.where("store").equals(id).toArray();
    const promosToClear = promos.filter((p) => (p.siteId ?? "site_us") === siteId);
    for (const promo of promosToClear) {
      await db.promotions.put({ ...promo, store: "" });
    }
  }
}

export async function getShopName(id: string): Promise<string> {
  const shop = await db.shops.get(id);
  return shop?.name ?? id;
}

/** 店铺数据量口径：该店铺下的 MSKU 数量。
 *  mskuStores 按行记录店铺 → 逐个 MSKU 计数；
 *  无 mskuStores 时回退整条记录的 store 字段，MSKU 列表按逗号拆分计数。 */
export async function getShopDataCount(shopId: string): Promise<number> {
  const shop = await db.shops.get(shopId);
  if (!shop) return 0;
  const skus = await db.skuMaster.toArray();
  return countShopMskus(skus, shop.name);
}

// ==================== Bulk operations ====================
/** 导入来自文件的 SKU 时，站点必须是已知站点，否则回退当前站，避免写出幽灵站点记录 */
async function enforceKnownSites<T extends { siteId?: string }>(rows: T[], currentSiteId: string): Promise<T[]> {
  const known = new Set((await db.sites.toArray()).map((s) => s.id));
  if (known.size === 0) known.add("site_us");
  return rows.map((r) => {
    const sid = r.siteId && known.has(r.siteId) ? r.siteId : currentSiteId;
    return sid === r.siteId ? r : { ...r, siteId: sid };
  });
}

export async function upsertSkuMaster(rows: SkuMaster[]): Promise<void> {
  if (rows.length === 0) return;
  const currentSiteId = await getCurrentSiteId();
  const withSiteId = await enforceKnownSites(rows, currentSiteId);
  await db.skuMaster.bulkPut(withSiteId);
}

/**
 * 部分更新模式：仅覆盖新数据中有值的字段，空值/0 不覆盖已有数据。
 * 适用于「只改部分参数」场景——Excel 中空单元格不会清空已有 FOB/售价等固定信息。
 *
 * 合并策略：
 * - 数值字段：新值 > 0 才覆盖，否则保留旧值
 * - 字符串字段：新值非空才覆盖，否则保留旧值
 * - 数组字段（competitorUrls/mskuStores 等）：新值有元素才覆盖
 * - 新 SKU（数据库中不存在）：直接写入，不合并
 */
export async function upsertSkuMasterPartial(rows: SkuMaster[]): Promise<void> {
  if (rows.length === 0) return;

  const currentSiteId = await getCurrentSiteId();
  const coerced = await enforceKnownSites(rows, currentSiteId);
  const compoundKeys = coerced.map((r) => [r.sku, r.siteId || currentSiteId] as [string, string]);
  const existing = await db.skuMaster.bulkGet(compoundKeys);
  const existingMap = new Map<string, SkuMaster>();
  for (let i = 0; i < existing.length; i++) {
    const e = existing[i];
    if (e) {
      const [keySku, keySite] = compoundKeys[i];
      existingMap.set(`${keySku}__${keySite}`, e);
    }
  }

  // 数值字段：新值 > 0 才覆盖
  const numFields: (keyof SkuMaster)[] = [
    "price", "listPrice", "costFob", "costShipping", "costDelivery",
    "costCommission", "commissionRate", "costStorage", "costReturn", "costAd",
    "discountPrice", "discountFob", "discountShipping", "discountDelivery",
    "discountCommission", "discountStorage", "discountReturn", "discountAd", "discountCoupon",
    "fbaPrice", "fbmPrice", "fbaLeadTimeDays", "fbmLeadTimeDays",
    "fbaSafetyStockDays", "fbmSafetyStockDays", "leadTimeDays", "safetyStockDays", "moq",
    "packageLength", "packageWidth", "packageHeight", "packageWeight", "unitsPerBox",
  ];

  // 字符串字段：新值非空才覆盖
  const strFields: (keyof SkuMaster)[] = [
    "name", "asin", "upc", "category", "launchDate", "parentGroup",
    "parentAsin", "parentSku", "productUrl", "marketplace", "image",
    "composeFormula", "factoryName",
  ];

  const merged: SkuMaster[] = coerced.map((row) => {
    const keySite = row.siteId || currentSiteId;
    const old = existingMap.get(`${row.sku}__${keySite}`);
    if (!old) return row; // 新 SKU，直接写入

    const result: SkuMaster = { ...old };

    // 数值字段：新值 > 0 才覆盖
    for (const f of numFields) {
      const newVal = row[f] as number | undefined;
      if (newVal != null && newVal > 0) {
        (result as unknown as Record<string, unknown>)[f] = newVal;
      }
    }

    // 字符串字段：新值非空才覆盖
    for (const f of strFields) {
      const newVal = row[f] as string | undefined;
      if (!newVal || newVal.trim() === "" || newVal === "-") continue;
      // 品名空单元格在解析层会回退为 SKU 编码占位，不能据此覆盖已有真实品名
      if (f === "name" && newVal === row.sku) {
        const oldName = (result as unknown as Record<string, unknown>)["name"];
        if (oldName && oldName !== row.sku) continue;
      }
      (result as unknown as Record<string, unknown>)[f] = newVal;
    }

    // 枚举/特殊字段
    if (row.fulfillment && row.fulfillment !== old.fulfillment) {
      result.fulfillment = row.fulfillment;
    }
    if (row.saleStatus && row.saleStatus !== old.saleStatus) {
      result.saleStatus = row.saleStatus;
    }
    if (row.linkType) {
      result.linkType = row.linkType;
    }
    if (row.lifecycle) {
      result.lifecycle = row.lifecycle;
    }
    if (row.fulfillmentMode) {
      result.fulfillmentMode = row.fulfillmentMode;
    }
    if (row.aPlus) {
      result.aPlus = row.aPlus;
    }

    // store：新值非空且非占位符才覆盖
    if (row.store && row.store !== "-" && row.store.startsWith("shop_")) {
      result.store = row.store;
    }

    // msku：新值非空才覆盖
    if (row.msku && row.msku.trim() !== "") {
      result.msku = row.msku;
    }

    // 数组/Record 字段：新值有键才覆盖
    if (row.competitorUrls && row.competitorUrls.length > 0) {
      result.competitorUrls = row.competitorUrls;
    }
    if (row.mskuStores && Object.keys(row.mskuStores).length > 0) {
      result.mskuStores = row.mskuStores;
    }
    if (row.mskuAsins && Object.keys(row.mskuAsins).length > 0) {
      result.mskuAsins = row.mskuAsins;
    }
    if (row.mskuMetrics && Object.keys(row.mskuMetrics).length > 0) {
      result.mskuMetrics = row.mskuMetrics;
    }
    if (row.mskuLinkTypes && Object.keys(row.mskuLinkTypes).length > 0) {
      result.mskuLinkTypes = row.mskuLinkTypes;
    }

    return result;
  });

  const withSiteId = merged.map((r) => ({ ...r, siteId: r.siteId || currentSiteId }));
  await db.skuMaster.bulkPut(withSiteId);
}

export async function upsertSnapshots(rows: DailySnapshot[]): Promise<void> {
  if (rows.length === 0) return;
  // Normalize: adRatio must always be positive, ensure siteId is set
  const siteId = rows[0].siteId || "site_us";
  const normalized = rows.map((r) => ({ ...r, adRatio: Math.abs(r.adRatio ?? 0), siteId: r.siteId || siteId }));
  const keys = new Set(normalized.map((r) => `${r.sku}__${r.date}__${r.siteId}`));
  const existing = await db.dailySnapshot
    .where("date")
    .equals(normalized[0].date)
    .filter((e) => (e.siteId || "site_us") === siteId)
    .toArray();
  const toDelete = existing.filter((e) => keys.has(`${e.sku}__${e.date}__${e.siteId || "site_us"}`));

  // Merge: for existing entries with same SKU+date, keep non-zero values from both sides
  const merged: DailySnapshot[] = [];
  const existingMap = new Map(toDelete.map((e) => [`${e.sku}__${e.date}__${e.siteId || "site_us"}`, e]));
  for (const row of normalized) {
    const key = `${row.sku}__${row.date}__${row.siteId}`;
    const old = existingMap.get(key);
    if (old) {
      merged.push({
        ...old,
        siteId: row.siteId,
        // New data fills gaps: only overwrite if old is 0/empty and new has value
        dailySales7d: row.dailySales7d || old.dailySales7d,
        monthlySales: row.monthlySales || old.monthlySales,
        adRatio: row.adRatio || old.adRatio,
        rating: row.rating || old.rating,
        reviewCount: row.reviewCount ?? old.reviewCount,
        returnRate: row.returnRate || old.returnRate,
        refundRate: row.refundRate ?? old.refundRate,
        adSpend: row.adSpend || old.adSpend,
        stockOnHand: row.stockOnHand || old.stockOnHand,
        stockInTransit: row.stockInTransit || old.stockInTransit,
        profit: row.profit || old.profit,
        profitMargin: row.profitMargin || old.profitMargin,
        totalCost: row.totalCost || old.totalCost,
        // 流量/转化字段（新模板 Sessions/订单量）：新值非 0 才覆盖，避免重导入时被旧值或 0 冲掉
        sessions: row.sessions || old.sessions,
        orders: row.orders || old.orders,
      });
    } else {
      merged.push(row);
    }
  }
  if (toDelete.length > 0) {
    await db.dailySnapshot.bulkDelete(
      toDelete.map((e) => e.id as number).filter((id) => id != null)
    );
  }
  await db.dailySnapshot.bulkAdd(merged);
}

export async function upsertInventoryLayers(rows: InventoryLayer[]): Promise<void> {
  if (rows.length === 0) return;
  const siteId = rows[0].siteId || "site_us";
  const normalized = rows.map((r) => ({ ...r, siteId: r.siteId || siteId }));
  const keys = new Set(normalized.map((r) => `${r.sku}__${r.date}__${r.siteId}`));
  const existing = await db.inventoryLayer
    .where("date")
    .equals(normalized[0].date)
    .filter((e) => (e.siteId || "site_us") === siteId)
    .toArray();
  const toDelete = existing.filter((e) => keys.has(`${e.sku}__${e.date}__${e.siteId || "site_us"}`));
  if (toDelete.length > 0) {
    await db.inventoryLayer.bulkDelete(
      toDelete.map((e) => e.id as number).filter((id) => id != null)
    );
  }
  await db.inventoryLayer.bulkAdd(normalized);
}

/**
 * 部分更新模式：仅覆盖新数据中有值的库存字段，空值/0 不覆盖已有数据。
 * 适用于「只改部分参数」场景——Excel 中空单元格不会清空已有海外仓/FBA/FBM 库存。
 *
 * 合并策略：
 * - 数值字段：新值 > 0 才覆盖，否则保留旧值
 * - 数组字段（warehouseBreakdown/transitBatches/factoryBatches）：新值有元素才覆盖
 * - 新 SKU（数据库中不存在）：直接写入，不合并
 */
export async function upsertInventoryLayersPartial(rows: InventoryLayer[]): Promise<void> {
  if (rows.length === 0) return;

  const siteId = rows[0].siteId || "site_us";
  const normalized = rows.map((r) => ({ ...r, siteId: r.siteId || siteId }));

  // 批量查询已有记录（按 date + siteId 查找，不跨站）
  const date = normalized[0].date;
  const existing = await db.inventoryLayer
    .where("date")
    .equals(date)
    .filter((e) => (e.siteId || "site_us") === siteId)
    .toArray();
  const existingMap = new Map<string, InventoryLayer>();
  for (const e of existing) {
    existingMap.set(`${e.sku}__${e.date}__${e.siteId || "site_us"}`, e);
  }

  // FIX: 查询每个 SKU 的最近一次库存记录（不含当前日期），用于继承 FBM/海外仓等数据
  const skuList = [...new Set(normalized.map((r) => r.sku))];
  const allPrevInventory = await db.inventoryLayer
    .where("sku")
    .anyOf(skuList)
    .and((r) => r.date !== date && (r.siteId || "site_us") === siteId)
    .toArray();
  const latestPrevBySku = new Map<string, InventoryLayer>();
  for (const inv of allPrevInventory) {
    const cur = latestPrevBySku.get(inv.sku);
    if (!cur || cur.date < inv.date) {
      latestPrevBySku.set(inv.sku, inv);
    }
  }

  // 数值字段：新值 > 0 才覆盖
  const numFields: (keyof InventoryLayer)[] = [
    "fbaStock", "fbmStock", "factoryStock",
    "eastTransit", "westTransit", "southeast", "southcentral",
    "eastStock", "westStock", "southeastStock", "southcentralStock",
    "southeastTransit", "southcentralTransit",
  ];

  const merged: InventoryLayer[] = normalized.map((row) => {
    const key = `${row.sku}__${row.date}__${row.siteId}`;
    const old = existingMap.get(key);
    if (!old) {
      // 新日期的记录：从上一次库存记录继承 FBM/海外仓数据
      const prev = latestPrevBySku.get(row.sku);
      if (prev) {
        const result: InventoryLayer = { ...row };
        // 数值字段：新值为 0/空时从上一次记录继承
        for (const f of numFields) {
          const newVal = result[f] as number | undefined;
          if (newVal == null || newVal <= 0) {
            const prevVal = prev[f] as number | undefined;
            if (prevVal != null && prevVal > 0) {
              (result as unknown as Record<string, unknown>)[f] = prevVal;
            }
          }
        }
        // warehouseBreakdown：新值为空时从上一次记录继承
        if ((!result.warehouseBreakdown || result.warehouseBreakdown.length === 0) && prev.warehouseBreakdown && prev.warehouseBreakdown.length > 0) {
          result.warehouseBreakdown = prev.warehouseBreakdown;
        }
        // transitBatches：新值为空时从上一次记录继承
        if ((!result.transitBatches || result.transitBatches.length === 0) && prev.transitBatches && prev.transitBatches.length > 0) {
          result.transitBatches = prev.transitBatches;
        }
        // factoryBatches：新值为空时从上一次记录继承
        if ((!result.factoryBatches || result.factoryBatches.length === 0) && prev.factoryBatches && prev.factoryBatches.length > 0) {
          result.factoryBatches = prev.factoryBatches;
        }
        return result;
      }
      return row; // 无历史记录，直接写入
    }

    const result: InventoryLayer = { ...old, siteId: row.siteId };
    const prev = latestPrevBySku.get(row.sku);

    // 数值字段：新值 > 0 才覆盖；新值为 0 且旧值也为 0 时，从上一次记录继承（修复历史脏数据）
    for (const f of numFields) {
      const newVal = row[f] as number | undefined;
      if (newVal != null && newVal > 0) {
        (result as unknown as Record<string, unknown>)[f] = newVal;
      } else {
        const oldVal = result[f] as number | undefined;
        if ((oldVal == null || oldVal <= 0) && prev) {
          const prevVal = prev[f] as number | undefined;
          if (prevVal != null && prevVal > 0) {
            (result as unknown as Record<string, unknown>)[f] = prevVal;
          }
        }
      }
    }

    // 数组字段：新值有元素才覆盖；新值为空且旧值也为空时，从上一次记录继承
    if (row.warehouseBreakdown && row.warehouseBreakdown.length > 0) {
      result.warehouseBreakdown = row.warehouseBreakdown;
    } else if ((!result.warehouseBreakdown || result.warehouseBreakdown.length === 0) && prev?.warehouseBreakdown && prev.warehouseBreakdown.length > 0) {
      result.warehouseBreakdown = prev.warehouseBreakdown;
    }
    if (row.transitBatches && row.transitBatches.length > 0) {
      result.transitBatches = row.transitBatches;
    } else if ((!result.transitBatches || result.transitBatches.length === 0) && prev?.transitBatches && prev.transitBatches.length > 0) {
      result.transitBatches = prev.transitBatches;
    }
    if (row.factoryBatches && row.factoryBatches.length > 0) {
      result.factoryBatches = row.factoryBatches;
    } else if ((!result.factoryBatches || result.factoryBatches.length === 0) && prev?.factoryBatches && prev.factoryBatches.length > 0) {
      result.factoryBatches = prev.factoryBatches;
    }

    return result;
  });

  // 删除已有记录（用合并后的数据替换，不跨站）
  const toDelete = existing.filter((e) => {
    const key = `${e.sku}__${e.date}__${e.siteId || "site_us"}`;
    return normalized.some((r) => `${r.sku}__${r.date}__${r.siteId}` === key);
  });
  if (toDelete.length > 0) {
    await db.inventoryLayer.bulkDelete(
      toDelete.map((e) => e.id as number).filter((id) => id != null)
    );
  }
  await db.inventoryLayer.bulkAdd(merged);
}

export async function clearAllData(): Promise<void> {
  await db.transaction(
    "rw",
    [
      db.skuMaster,
      db.dailySnapshot,
      db.inventoryLayer,
      db.campaigns,
      db.promotions,
      db.manualPromotions,
      db.alerts,
      db.config,
      db.warehouseProviders,
      db.estimates,
      db.todos,
      db.calculationRecords,
      db.shops,
      db.opsLogs,
      db.warehouseMappings,
    ],
    async () => {
      await db.skuMaster.clear();
      await db.dailySnapshot.clear();
      await db.inventoryLayer.clear();
      await db.campaigns.clear();
      await db.promotions.clear();
      await db.manualPromotions.clear();
      await db.alerts.clear();
      await db.config.clear();
      await db.warehouseProviders.clear();
      await db.estimates.clear();
      await db.todos.clear();
      await db.calculationRecords.clear();
      await db.shops.clear();
      await db.opsLogs.clear();
      await db.warehouseMappings.clear();
    }
  );
}

/** Export the whole database as one JSON blob for cloud sync. */
export async function exportSnapshot(): Promise<{
  skuMaster: SkuMaster[];
  dailySnapshot: DailySnapshot[];
  inventoryLayer: InventoryLayer[];
  campaigns: Campaign[];
  promotions: Promotion[];
  manualPromotions: ManualPromotion[];
  alerts: Alert[];
  config: { key: string; value: unknown }[];
  shops: Shop[];
  opsLogs: OpsLog[];
  warehouseMappings: WarehouseMapping[];
  exchangeRates: ExchangeRate[];
  diagnosisNotes: DiagnosisNote[];
  exportedAt: string;
}> {
  const [skuMaster, dailySnapshot, inventoryLayer, campaigns, promotions, manualPromotions, alerts, config, shops, opsLogs, warehouseMappings, exchangeRates, diagnosisNotes] =
    await Promise.all([
      db.skuMaster.toArray(),
      db.dailySnapshot.toArray(),
      db.inventoryLayer.toArray(),
      db.campaigns.toArray(),
      db.promotions.toArray(),
      db.manualPromotions.toArray(),
      db.alerts.toArray(),
      db.config.toArray(),
      db.shops.toArray(),
      db.opsLogs.toArray(),
      db.warehouseMappings.toArray(),
      db.exchangeRates.toArray(),
      db.diagnosisNotes.toArray(),
    ]);
  return {
    skuMaster,
    dailySnapshot,
    inventoryLayer,
    campaigns,
    promotions,
    manualPromotions,
    alerts,
    config,
    shops,
    opsLogs,
    warehouseMappings,
    exchangeRates,
    diagnosisNotes,
    exportedAt: new Date().toISOString(),
  };
}

/** Import a JSON blob back into the local DB. */
export async function importSnapshot(payload: {
  skuMaster?: SkuMaster[];
  dailySnapshot?: DailySnapshot[];
  inventoryLayer?: InventoryLayer[];
  campaigns?: Campaign[];
  promotions?: Promotion[];
  manualPromotions?: ManualPromotion[];
  alerts?: Alert[];
  config?: { key: string; value: unknown }[];
  shops?: Shop[];
  opsLogs?: OpsLog[];
  warehouseMappings?: WarehouseMapping[];
  exchangeRates?: ExchangeRate[];
  diagnosisNotes?: DiagnosisNote[];
}): Promise<void> {
  await db.transaction(
    "rw",
    [
      db.skuMaster,
      db.dailySnapshot,
      db.inventoryLayer,
      db.campaigns,
      db.promotions,
      db.manualPromotions,
      db.alerts,
      db.config,
      db.warehouseProviders,
      db.estimates,
      db.todos,
      db.calculationRecords,
      db.shops,
      db.opsLogs,
      db.warehouseMappings,
      db.exchangeRates,
      db.diagnosisNotes,
    ],
    async () => {
      if (payload.skuMaster) {
        await db.skuMaster.clear();
        await db.skuMaster.bulkPut(payload.skuMaster);
      }
      if (payload.dailySnapshot) {
        await db.dailySnapshot.clear();
        const rows = payload.dailySnapshot.map(({ id: _id, ...rest }) => rest);
        await db.dailySnapshot.bulkAdd(rows);
      }
      if (payload.inventoryLayer) {
        await db.inventoryLayer.clear();
        const rows = payload.inventoryLayer.map(({ id: _id, ...rest }) => rest);
        await db.inventoryLayer.bulkAdd(rows);
      }
      if (payload.campaigns) {
        await db.campaigns.clear();
        await db.campaigns.bulkPut(payload.campaigns);
      }
      if (payload.promotions) {
        await db.promotions.clear();
        await db.promotions.bulkPut(payload.promotions);
      }
      if (payload.manualPromotions) {
        await db.manualPromotions.clear();
        await db.manualPromotions.bulkPut(payload.manualPromotions);
      }
      if (payload.alerts) {
        await db.alerts.clear();
        await db.alerts.bulkPut(payload.alerts);
      }
      if (payload.config) {
        await db.config.clear();
        await db.config.bulkPut(payload.config);
      }
      if (payload.shops) {
        await db.shops.clear();
        await db.shops.bulkPut(payload.shops);
      }
      if (payload.opsLogs) {
        await db.opsLogs.clear();
        await db.opsLogs.bulkPut(payload.opsLogs);
      }
      if (payload.warehouseMappings) {
        await db.warehouseMappings.clear();
        await db.warehouseMappings.bulkPut(payload.warehouseMappings);
      }
      if (payload.exchangeRates) {
        await db.exchangeRates.clear();
        await db.exchangeRates.bulkPut(payload.exchangeRates);
      }
      if (payload.diagnosisNotes) {
        await db.diagnosisNotes.clear();
        await db.diagnosisNotes.bulkPut(payload.diagnosisNotes);
      }
    }
  );
  window.dispatchEvent(new CustomEvent(EXCHANGE_RATES_EVENT));
}
// ==================== OpsLog CRUD ====================
export async function getOpsLogs(sku?: string, siteId?: string): Promise<OpsLog[]> {
  let data = await db.opsLogs.toArray();
  if (sku) data = data.filter((l) => l.sku === sku);
  if (siteId) data = data.filter((l) => (l.siteId ?? "site_us") === siteId);
  return data.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
}

export async function addOpsLog(
  sku: string,
  date: string,
  action: string,
  detail: string,
  impact?: string,
  msku?: string,
  skuName?: string,
  siteId?: string,
): Promise<string> {
  const sid = siteId ?? (await getCurrentSiteId());
  const id = `opslog_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  await db.opsLogs.put({
    id,
    siteId: sid,
    sku,
    msku,
    skuName,
    date,
    action,
    detail,
    impact,
    createdAt: new Date().toISOString(),
  });
  return id;
}

export async function deleteOpsLog(id: string): Promise<void> {
  await db.opsLogs.delete(id);
}

// ==================== 异常诊断备注 CRUD ====================

/** 诊断备注变更事件：备注面板保存/删除后广播，概览页与列表同步刷新 */
export const DIAGNOSIS_NOTES_EVENT = "ops-diagnosis-notes-change";

export async function getDiagnosisNotes(filter?: {
  sku?: string;
  siteId?: string;
  anomalyType?: DiagnosisNote["anomalyType"];
}): Promise<DiagnosisNote[]> {
  let data = await db.diagnosisNotes.toArray();
  if (filter?.sku) data = data.filter((n) => n.sku === filter.sku);
  if (filter?.siteId) data = data.filter((n) => (n.siteId ?? "site_us") === filter.siteId);
  if (filter?.anomalyType) data = data.filter((n) => n.anomalyType === filter.anomalyType);
  return data.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
}

export async function addDiagnosisNote(input: {
  sku: string;
  skuName?: string;
  anomalyType: DiagnosisNote["anomalyType"];
  content: string;
  periodLabel?: string;
  siteId?: string;
}): Promise<string> {
  const sid = input.siteId ?? (await getCurrentSiteId());
  const id = `dnote_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  await db.diagnosisNotes.put({
    id,
    siteId: sid,
    sku: input.sku,
    skuName: input.skuName,
    anomalyType: input.anomalyType,
    content: input.content.trim(),
    periodLabel: input.periodLabel,
    createdAt: new Date().toISOString(),
  });
  window.dispatchEvent(new CustomEvent(DIAGNOSIS_NOTES_EVENT));
  return id;
}

export async function updateDiagnosisNote(id: string, content: string): Promise<void> {
  await db.diagnosisNotes.update(id, { content: content.trim(), updatedAt: new Date().toISOString() });
  window.dispatchEvent(new CustomEvent(DIAGNOSIS_NOTES_EVENT));
}

export async function deleteDiagnosisNote(id: string): Promise<void> {
  await db.diagnosisNotes.delete(id);
  window.dispatchEvent(new CustomEvent(DIAGNOSIS_NOTES_EVENT));
}

// ==================== Warehouse Mapping helpers ====================

/** 仓库映射/区域变更事件：映射面板保存后广播，展示页监听刷新别名/区域 */
export const WAREHOUSE_MAPPING_EVENT = "ops-warehouse-mapping-change";

function dispatchWarehouseMappingEvent() {
  window.dispatchEvent(new CustomEvent(WAREHOUSE_MAPPING_EVENT));
}

/** 智能猜测仓库名对应的区域 */
export function guessRegion(warehouseName: string): import("./types").WarehouseRegion | null {
  const name = warehouseName.toLowerCase();
  // 美东关键词
  if (/美东|east|nj|newjersey|新泽西|njf|njjw/.test(name)) return "east";
  // 美西关键词（\bcap\b 匹配独立单词 cap，兼容 "乐歌(新) CAP" 这类无尾随空格的仓库名）
  if (/美西|west|ca[^a-z]|cali|洛杉矶|cajw|\bcap\b/.test(name)) return "west";
  // 东南关键词
  if (/东南|southeast|sav|savannah|萨凡纳/.test(name)) return "southeast";
  // 中南关键词
  if (/中南|southcentral|hou|texas|tx|休斯顿|txjw/.test(name)) return "southcentral";
  return null;
}

/** 获取所有仓库映射 */
export async function getAllWarehouseMappings(): Promise<import("./types").WarehouseMapping[]> {
  return db.warehouseMappings.toArray();
}

/**
 * 自定义仓库区域列表（预置美东/美西/东南/中南四仓，可增删自定义区域）。
 * 存 config key "warehouseRegions"。
 */
export async function getWarehouseRegions(): Promise<import("./types").WarehouseRegionOption[]> {
  const row = await db.config.get("warehouseRegions");
  const list = row?.value as import("./types").WarehouseRegionOption[] | undefined;
  if (!Array.isArray(list) || list.length === 0) return [...DEFAULT_WAREHOUSE_REGIONS];
  // 预置四仓始终保留在前，防止旧版本升级后丢区域
  const codes = new Set(list.map((r) => r.code));
  const defaults = DEFAULT_WAREHOUSE_REGIONS.filter((d) => !codes.has(d.code));
  return [...defaults, ...list];
}

export async function setWarehouseRegions(list: import("./types").WarehouseRegionOption[]): Promise<void> {
  await db.config.put({ key: "warehouseRegions", value: list });
  dispatchWarehouseMappingEvent();
}

/**
 * 站点感知的仓库名→区域映射表。
 * 同一仓库名可按站点分别映射（如 US 站与 CA 站各有一套），
 * 站点专属映射优先于通用映射（siteId 为空视为通用）。
 */
export async function resolveRegionMap(siteId?: string): Promise<Map<string, import("./types").WarehouseRegion>> {
  const all = await db.warehouseMappings.toArray();
  const map = new Map<string, import("./types").WarehouseRegion>();
  for (const m of all) {
    if (m.siteId) continue; // 通用映射先入
    map.set(m.warehouseName, m.region);
  }
  if (siteId) {
    for (const m of all) {
      if (m.siteId === siteId) map.set(m.warehouseName, m.region);
    }
  }
  return map;
}

/** 批量获取仓库名→区域映射（不区分站点，兼容旧调用） */
export async function getWarehouseRegionMap(): Promise<Map<string, import("./types").WarehouseRegion>> {
  return resolveRegionMap(undefined);
}

/** 仓库名→自定义别名映射（站点专属优先于通用） */
export async function getWarehouseAliasMap(siteId?: string): Promise<Map<string, string>> {
  const all = await db.warehouseMappings.toArray();
  const map = new Map<string, string>();
  for (const m of all) {
    if (!m.alias?.trim()) continue;
    if (m.siteId) continue;
    map.set(m.warehouseName, m.alias.trim());
  }
  if (siteId) {
    for (const m of all) {
      if (m.siteId !== siteId || !m.alias?.trim()) continue;
      map.set(m.warehouseName, m.alias.trim());
    }
  }
  return map;
}

/** 保存或更新单条仓库映射（按 仓库名+站点 唯一），并自动重算已有库存数据的区域字段 */
export async function upsertWarehouseMapping(
  warehouseName: string,
  region: import("./types").WarehouseRegion,
  opts?: { siteId?: string | null; alias?: string | null },
): Promise<void> {
  const siteId = opts?.siteId?.trim() || undefined;
  const alias = opts?.alias != null ? (opts.alias.trim() || undefined) : undefined;
  const all = await db.warehouseMappings.where("warehouseName").equals(warehouseName).toArray();
  const existing = all.find((m) => (m.siteId ?? undefined) === siteId);
  if (existing) {
    await db.warehouseMappings.update(existing.id!, {
      region,
      ...(opts?.siteId !== undefined ? { siteId } : {}),
      ...(opts?.alias !== undefined ? { alias } : {}),
    });
  } else {
    await db.warehouseMappings.add({
      warehouseName,
      region,
      siteId,
      alias,
      createdAt: new Date().toISOString(),
    });
  }
  await reapplyWarehouseMappings();
  dispatchWarehouseMappingEvent();
}

/** 更新单条映射的部分字段（站点/别名），不动区域 */
export async function patchWarehouseMapping(
  id: number,
  patch: { siteId?: string | null; alias?: string | null },
): Promise<void> {
  const value: Record<string, string | undefined> = {};
  if (patch.siteId !== undefined) value.siteId = patch.siteId?.trim() || undefined;
  if (patch.alias !== undefined) value.alias = patch.alias?.trim() || undefined;
  await db.warehouseMappings.update(id, value);
  await reapplyWarehouseMappings();
  dispatchWarehouseMappingEvent();
}

/** 删除仓库映射，并自动重算已有库存数据的区域字段 */
export async function deleteWarehouseMapping(id: number): Promise<void> {
  await db.warehouseMappings.delete(id);
  await reapplyWarehouseMappings();
  dispatchWarehouseMappingEvent();
}

/**
 * 重新应用仓库映射：遍历所有 inventoryLayer 记录，
 * 根据 warehouseBreakdown 中的仓库名 + 当前映射（按该层站点解析）重算四仓区域字段。
 * 自定义区域不写入四仓硬编码字段，仅体现在仓库明细与总库存汇总中。
 * 在映射变更后自动调用，无需重新导入数据。
 */
export async function reapplyWarehouseMappings(): Promise<number> {
  const allInv = await db.inventoryLayer.toArray();
  if (!allInv.some((inv) => inv.warehouseBreakdown?.length)) return 0;
  const genericMap = await resolveRegionMap(undefined);
  const siteMaps = new Map<string, Map<string, import("./types").WarehouseRegion>>();
  let updated = 0;
  for (const inv of allInv) {
    if (!inv.warehouseBreakdown?.length) continue;
    const sid = inv.siteId ?? "";
    let regionMap = siteMaps.get(sid);
    if (!regionMap) {
      regionMap = sid ? await resolveRegionMap(sid) : genericMap;
      siteMaps.set(sid, regionMap);
    }
    let eastStock = 0, westStock = 0, southeastStock = 0, southcentralStock = 0;
    for (const wb of inv.warehouseBreakdown) {
      const region = regionMap.get(wb.warehouse);
      if (!region) continue;
      switch (region) {
        case "east": eastStock += wb.qty; break;
        case "west": westStock += wb.qty; break;
        case "southeast": southeastStock += wb.qty; break;
        case "southcentral": southcentralStock += wb.qty; break;
      }
    }
    await db.inventoryLayer.update(inv.id!, {
      eastStock, westStock, southeastStock, southcentralStock,
    });
    updated++;
  }
  return updated;
}

/**
 * 按店铺名查找已有店铺，不存在则自动创建，返回 shopId。
 * 用于导入时自动匹配/创建店铺，与 addShop 逻辑一致。
 */
export async function getOrCreateShopByName(name: string): Promise<string> {
  const trimmed = name.trim();
  if (!trimmed) return "-";
  const all = await db.shops.toArray();
  const existing = all.find(s => s.name === trimmed);
  if (existing) return existing.id;
  const shop = await addShop(trimmed);
  return shop.id;
}

// ==================== Site management (multi-site) ====================
export const DEFAULT_SITES: Site[] = [
  {
    id: "site_us",
    name: "美国站",
    marketplace: "US",
    currency: "USD",
    currencySymbol: "$",
    exchangeRateToUsd: 1.0,
    cnyToUsdRate: 7.25,
    commissionRate: 15,
    fbaDeliveryFee: 3.5,
    vatRate: 0,
    isActive: true,
    sortOrder: 1,
    createdAt: new Date().toISOString(),
  },
  {
    id: "site_uk",
    name: "英国站",
    marketplace: "UK",
    currency: "GBP",
    currencySymbol: "£",
    exchangeRateToUsd: 1.27,
    cnyToUsdRate: 7.25,
    commissionRate: 15,
    fbaDeliveryFee: 2.5,
    vatRate: 20,
    isActive: false,
    sortOrder: 2,
    createdAt: new Date().toISOString(),
  },
  {
    id: "site_de",
    name: "德国站",
    marketplace: "DE",
    currency: "EUR",
    currencySymbol: "€",
    exchangeRateToUsd: 1.08,
    cnyToUsdRate: 7.25,
    commissionRate: 15,
    fbaDeliveryFee: 3.0,
    vatRate: 19,
    isActive: false,
    sortOrder: 3,
    createdAt: new Date().toISOString(),
  },
  {
    id: "site_jp",
    name: "日本站",
    marketplace: "JP",
    currency: "JPY",
    currencySymbol: "¥",
    exchangeRateToUsd: 0.0067,
    cnyToUsdRate: 7.25,
    commissionRate: 15,
    fbaDeliveryFee: 500,
    vatRate: 10,
    isActive: false,
    sortOrder: 4,
    createdAt: new Date().toISOString(),
  },
  {
    id: "site_ca",
    name: "加拿大站",
    marketplace: "CA",
    currency: "CAD",
    currencySymbol: "C$",
    exchangeRateToUsd: 0.73,
    cnyToUsdRate: 7.25,
    commissionRate: 15,
    fbaDeliveryFee: 4.5,
    vatRate: 5,
    isActive: false,
    sortOrder: 5,
    createdAt: new Date().toISOString(),
  },
];

export const DEFAULT_SITE_CONFIG: SiteConfig = {
  siteId: "",
  defaultLeadTime: 40,
  defaultSafetyStockDays: 30,
  defaultTargetCoverDays: 60,
  profitMarginThreshold: 5,
  adRatioThreshold: 30,
  ratingDropThreshold: 0.2,
  returnRateThreshold: 5,
  lifecycleNewDays: 90,
  lifecycleGrowthDays: 180,
};

export async function ensureDefaultSites(): Promise<void> {
  const count = await db.sites.count();
  if (count > 0) return;
  await db.sites.bulkPut(DEFAULT_SITES);
}

export async function getAllSites(): Promise<Site[]> {
  return db.sites.orderBy("sortOrder").toArray();
}

export async function getActiveSites(): Promise<Site[]> {
  return db.sites.where("isActive").equals(1 as never).toArray()
    .catch(() => db.sites.toArray());
}

export async function getSite(id: string): Promise<Site | undefined> {
  return db.sites.get(id);
}

export async function addSite(site: Omit<Site, "id" | "createdAt">): Promise<Site> {
  const newSite: Site = {
    ...site,
    id: `site_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    createdAt: new Date().toISOString(),
  };
  await db.sites.put(newSite);
  return newSite;
}

export async function updateSite(id: string, updates: Partial<Site>): Promise<void> {
  const site = await db.sites.get(id);
  if (!site) throw new Error("站点不存在");
  await db.sites.put({ ...site, ...updates, updatedAt: new Date().toISOString() });
}

export async function deleteSite(id: string): Promise<void> {
  if (id === "site_us") throw new Error("默认站点不可删除");
  await db.sites.delete(id);
  // skuMaster uses compound key [sku+siteId], can't modify primary key via .modify()
  const siteSkus = await db.skuMaster.where("siteId").equals(id).toArray();
  if (siteSkus.length > 0) {
    await db.skuMaster.bulkDelete(siteSkus.map(s => [s.sku, s.siteId!] as [string, string]));
    // site_us 中可能已存在同 [sku, site_us] 记录，直接 bulkPut 会覆盖；冲突时跳过，保留 site_us 原记录
    const existingSiteUs = await db.skuMaster.bulkGet(siteSkus.map(s => [s.sku, "site_us"] as [string, string]));
    const toPut = siteSkus.filter((_, i) => !existingSiteUs[i]).map(s => ({ ...s, siteId: "site_us" }));
    if (toPut.length > 0) await db.skuMaster.bulkPut(toPut);
  }
  await db.shops.where("siteId").equals(id).modify({ siteId: "site_us" });
  await db.promotions.where("siteId").equals(id).modify({ siteId: "site_us" });
  await db.dailySnapshot.where("siteId").equals(id).modify({ siteId: "site_us" });
  await db.inventoryLayer.where("siteId").equals(id).modify({ siteId: "site_us" });
  await db.campaigns.where("siteId").equals(id).modify({ siteId: "site_us" });
  await db.manualPromotions.where("siteId").equals(id).modify({ siteId: "site_us" });
  await db.alerts.where("siteId").equals(id).modify({ siteId: "site_us" });
  await db.calculationRecords.where("siteId").equals(id).modify({ siteId: "site_us" });
  await db.todos.where("siteId").equals(id).modify({ siteId: "site_us" });
  await db.opsLogs.where("siteId").equals(id).modify({ siteId: "site_us" });
}

export async function getCurrentSiteId(): Promise<string> {
  const row = await db.config.get("currentSiteId");
  return (row?.value as string) ?? "site_us";
}

export async function setCurrentSiteId(siteId: string): Promise<void> {
  await db.config.put({ key: "currentSiteId", value: siteId });
}

export async function getSiteConfig(siteId: string): Promise<SiteConfig> {
  const row = await db.config.get(`siteConfig_${siteId}`);
  return (row?.value as SiteConfig) ?? { ...DEFAULT_SITE_CONFIG, siteId };
}

export async function setSiteConfig(cfg: SiteConfig): Promise<void> {
  await db.config.put({ key: `siteConfig_${cfg.siteId}`, value: cfg });
}

// ==================== 多币种汇率（任意货币对，用户自定义） ====================
export const EXCHANGE_RATES_EVENT = "ops-rates-change";
export const DEFAULT_FOB_CURRENCY = "CNY";

export function normalizeCurrencyCode(code: string): string {
  return String(code ?? "").trim().toUpperCase().slice(0, 8);
}

export const DEFAULT_EXCHANGE_RATES: ExchangeRate[] = [
  { id: "USD_CNY", from: "USD", to: "CNY", rate: 7.25, updatedAt: "" },
  { id: "GBP_USD", from: "GBP", to: "USD", rate: 1.27, updatedAt: "" },
  { id: "EUR_USD", from: "EUR", to: "USD", rate: 1.08, updatedAt: "" },
  { id: "JPY_USD", from: "JPY", to: "USD", rate: 0.0067, updatedAt: "" },
  { id: "CAD_USD", from: "CAD", to: "USD", rate: 0.73, updatedAt: "" },
];

/** 全新数据库时填充默认汇率（已有数据则不覆盖，尊重用户自定义） */
export async function ensureDefaultExchangeRates(): Promise<void> {
  const count = await db.exchangeRates.count();
  if (count > 0) return;
  const now = new Date().toISOString();
  await db.exchangeRates.bulkPut(DEFAULT_EXCHANGE_RATES.map((r) => ({ ...r, updatedAt: now })));
}

export async function getAllExchangeRates(): Promise<ExchangeRate[]> {
  const rows = await db.exchangeRates.toArray();
  return rows.sort((a, b) =>
    a.from === b.from ? a.to.localeCompare(b.to) : a.from.localeCompare(b.from)
  );
}

export async function upsertExchangeRate(from: string, to: string, rate: number): Promise<void> {
  const f = normalizeCurrencyCode(from);
  const t = normalizeCurrencyCode(to);
  if (!f || !t) throw new Error("货币代码不能为空");
  if (f === t) throw new Error("源货币与目标货币相同");
  if (!(Number.isFinite(rate) && rate > 0)) throw new Error("汇率必须为正数");
  const row: ExchangeRate = {
    id: `${f}_${t}`,
    from: f,
    to: t,
    rate,
    updatedAt: new Date().toISOString(),
  };
  await db.exchangeRates.put(row);
  window.dispatchEvent(new CustomEvent(EXCHANGE_RATES_EVENT));
}

export async function deleteExchangeRate(id: string): Promise<void> {
  await db.exchangeRates.delete(id);
  window.dispatchEvent(new CustomEvent(EXCHANGE_RATES_EVENT));
}

export type RateGraph = Map<string, Array<[string, number]>>;

/** 把汇率对列表构建为无向图：A→B 为 rate，则 B→A 自动取 1/rate */
export function buildRateGraph(rows: ExchangeRate[]): RateGraph {
  const graph: RateGraph = new Map();
  for (const r of rows) {
    const f = normalizeCurrencyCode(r.from);
    const t = normalizeCurrencyCode(r.to);
    const rate = Number(r.rate);
    if (!f || !t || f === t || !(rate > 0)) continue;
    if (!graph.has(f)) graph.set(f, []);
    if (!graph.has(t)) graph.set(t, []);
    graph.get(f)!.push([t, rate]);
    graph.get(t)!.push([f, 1 / rate]);
  }
  return graph;
}

/** 沿汇率图寻找路径换算（支持任意中转，如 GBP→USD→CNY）；无路径返回 null */
export function convertWithGraph(amount: number, from: string, to: string, graph: RateGraph): number | null {
  const f = normalizeCurrencyCode(from);
  const t = normalizeCurrencyCode(to);
  if (!f || !t || !Number.isFinite(amount)) return null;
  if (f === t) return amount;
  const queue: Array<[string, number]> = [[f, 1]];
  const visited = new Set<string>([f]);
  while (queue.length > 0) {
    const [cur, factor] = queue.shift()!;
    const edges = graph.get(cur);
    if (!edges) continue;
    for (const [next, rate] of edges) {
      if (next === t) return amount * factor * rate;
      if (visited.has(next)) continue;
      visited.add(next);
      queue.push([next, factor * rate]);
    }
  }
  return null;
}

export async function convertCurrency(amount: number, from: string, to: string): Promise<number | null> {
  const rows = await db.exchangeRates.toArray();
  return convertWithGraph(amount, from, to, buildRateGraph(rows));
}

/** FOB 成本录入币种（默认人民币），SKU 页据此换算到站点货币 */
export async function getFobSourceCurrency(): Promise<string> {
  const row = await db.config.get("fobSourceCurrency");
  const v = normalizeCurrencyCode(String(row?.value ?? ""));
  return v || DEFAULT_FOB_CURRENCY;
}

export async function setFobSourceCurrency(code: string): Promise<void> {
  const v = normalizeCurrencyCode(code);
  if (!v) throw new Error("货币代码不能为空");
  await db.config.put({ key: "fobSourceCurrency", value: v });
  window.dispatchEvent(new CustomEvent(EXCHANGE_RATES_EVENT));
}

export async function getCrossSiteSummary(): Promise<CrossSiteReport> {
  const sites = await getAllSites();
  const summaries: CrossSiteSummary[] = [];

  for (const site of sites.filter(s => s.isActive)) {
    const [skus, snapshots, alerts] = await Promise.all([
      db.skuMaster.where("siteId").equals(site.id).count(),
      db.dailySnapshot.where("siteId").equals(site.id).toArray(),
      db.alerts.where("siteId").equals(site.id).count(),
    ]);

    const latestSnapshots = new Map<string, typeof snapshots[0]>();
    for (const s of snapshots) {
      if (!latestSnapshots.has(s.sku)) latestSnapshots.set(s.sku, s);
    }

    let totalSalesUsd = 0;
    let totalProfitUsd = 0;
    let totalStock = 0;

    for (const snap of latestSnapshots.values()) {
      totalSalesUsd += (snap.monthlySales || 0) * site.exchangeRateToUsd;
      totalProfitUsd += (snap.profit || 0) * site.exchangeRateToUsd;
      totalStock += snap.stockOnHand || 0;
    }

    summaries.push({
      siteId: site.id,
      siteName: site.name,
      currency: site.currency,
      exchangeRateToUsd: site.exchangeRateToUsd,
      totalSalesUsd,
      totalProfitUsd,
      totalSkuCount: skus,
      totalStockOnHand: totalStock,
      alertCount: alerts,
    });
  }

  return {
    sites: summaries,
    grandTotalSalesUsd: summaries.reduce((s, x) => s + x.totalSalesUsd, 0),
    grandTotalProfitUsd: summaries.reduce((s, x) => s + x.totalProfitUsd, 0),
    grandTotalSkuCount: summaries.reduce((s, x) => s + x.totalSkuCount, 0),
    grandTotalAlertCount: summaries.reduce((s, x) => s + x.alertCount, 0),
  };
}
